import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound, Loader2, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { PATHS } from '@/app/router/routes'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { PasswordInput } from '@/shared/ui/password-input'
import { extractApiError, handleMutationError } from '@/shared/utils/handle-api-error'
import LayoutAuth from '../components/layout-auth'
import useAuth from '../hooks/use-auth'
import { passwordRecoveryV2Service } from '../password-recovery-v2.service'

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(12, 'Usa al menos 12 caracteres.')
      .max(128, 'Usa como máximo 128 caracteres.'),
    confirmPassword: z.string().min(1, 'Confirma la nueva contraseña.')
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmPassword']
  })

type ResetPasswordValues = z.infer<typeof resetPasswordSchema>

function readRecoveryToken(): string {
  const raw = window.location.hash.slice(1)
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return ''
  }
}

export function ResetPasswordV2() {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const [token] = useState(readRecoveryToken)
  const [invalidLink, setInvalidLink] = useState(token.length === 0)
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<ResetPasswordValues>({
    defaultValues: { newPassword: '', confirmPassword: '' },
    resolver: zodResolver(resetPasswordSchema)
  })

  useEffect(() => {
    if (!window.location.hash) return
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`
    )
  }, [])

  if (auth?.id) return <Navigate to={PATHS.admin.root} replace />

  const submit = async (values: ResetPasswordValues) => {
    if (!token) {
      setInvalidLink(true)
      return
    }
    try {
      await passwordRecoveryV2Service.complete({ token, newPassword: values.newPassword })
      toast.success('Tu contraseña fue actualizada. Inicia sesión nuevamente.')
      navigate(PATHS.auth.login, { replace: true })
    } catch (error) {
      if (extractApiError(error)?.errorCode === 'PASSWORD_RECOVERY_INVALID') {
        setInvalidLink(true)
        return
      }
      handleMutationError(error)
    }
  }

  if (invalidLink) {
    return (
      <LayoutAuth title="El enlace ya no es válido">
        <Alert variant="destructive">
          <TriangleAlert className="size-4" />
          <AlertTitle>Solicita un enlace nuevo</AlertTitle>
          <AlertDescription>
            El enlace expiró, ya fue utilizado o quedó invalidado por otro cambio de contraseña.
          </AlertDescription>
        </Alert>
        <Button asChild>
          <Link to={PATHS.auth.forgotPassword}>Solicitar otro enlace</Link>
        </Button>
      </LayoutAuth>
    )
  }

  return (
    <LayoutAuth
      title="Crea una contraseña nueva"
      description="Al guardar, cerraremos todas tus sesiones y revocaremos tus API tokens. Tu configuración 2FA se conserva."
    >
      <form className="grid w-full grid-cols-1 gap-6" onSubmit={handleSubmit(submit)}>
        <Controller
          control={control}
          name="newPassword"
          render={({ field, fieldState }) => (
            <Field data-invalid={Boolean(fieldState.error)}>
              <FieldLabel htmlFor="staff-password-recovery-new-password">
                Nueva contraseña
              </FieldLabel>
              <PasswordInput
                {...field}
                id="staff-password-recovery-new-password"
                autoComplete="new-password"
              />
              <FieldDescription>Usa una frase única de entre 12 y 128 caracteres.</FieldDescription>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <Field data-invalid={Boolean(fieldState.error)}>
              <FieldLabel htmlFor="staff-password-recovery-confirm-password">
                Confirmar contraseña
              </FieldLabel>
              <PasswordInput
                {...field}
                id="staff-password-recovery-confirm-password"
                autoComplete="new-password"
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <KeyRound className="mr-2 size-4" />
          )}
          {isSubmitting ? 'Actualizando…' : 'Actualizar contraseña'}
        </Button>
      </form>
    </LayoutAuth>
  )
}
